import * as mammoth from 'mammoth';

/**
 * Extract text from a DOCX file using mammoth.js
 * @param file - The DOCX file to process
 * @param onProgress - Optional progress callback (0-100)
 * @returns Promise<string> - The extracted text
 */
export const extractTextFromDocx = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    // Report initial progress
    if (onProgress) onProgress(10);

    // Convert file to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    if (onProgress) onProgress(30);

    // Use mammoth to extract raw text from the DOCX file
    const result = await mammoth.extractRawText({ arrayBuffer });
    
    if (onProgress) onProgress(90);

    // Check for any messages/warnings from mammoth
    if (result.messages && result.messages.length > 0) {
      console.warn('Mammoth.js messages:', result.messages);
    }

    if (onProgress) onProgress(100);

    // Return the extracted text
    return result.value || '';
    
  } catch (error) {
    console.error('Error extracting text from DOCX:', error);
    throw new Error(`Failed to extract text from Word document: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Check if a file is a valid DOCX file based on its extension and MIME type
 * @param file - The file to check
 * @returns boolean - True if the file appears to be a DOCX file
 */
export const isDocxFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  const validExtensions = ['.docx'];
  const validMimeTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/octet-stream' // Sometimes DOCX files are detected as this
  ];

  const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
  const hasValidMimeType = validMimeTypes.includes(file.type);

  return hasValidExtension || hasValidMimeType;
};

/**
 * Check if a file is a valid XLSX file based on its extension and MIME type
 * @param file - The file to check
 * @returns boolean - True if the file appears to be an XLSX file
 */
export const isXlsxFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  const validMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/octet-stream', // Sometimes XLSX files are detected as this
  ];

  return fileName.endsWith('.xlsx') || validMimeTypes.includes(file.type);
};

/**
 * Get file type based on file extension and MIME type
 * @param file - The file to analyze
 * @returns 'pdf' | 'docx' | 'xlsx' | 'unknown'
 */
export const getFileType = (file: File): 'pdf' | 'docx' | 'xlsx' | 'unknown' => {
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith('.pdf') || file.type === 'application/pdf') {
    return 'pdf';
  }

  // Check extension-specific types before the generic octet-stream fallback.
  if (fileName.endsWith('.xlsx')) {
    return 'xlsx';
  }

  if (fileName.endsWith('.docx')) {
    return 'docx';
  }

  if (isDocxFile(file)) {
    return 'docx';
  }

  if (isXlsxFile(file)) {
    return 'xlsx';
  }

  return 'unknown';
};
